import React, { useMemo, type FC } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BlurView, type BlurTint, type BlurViewProps } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { buildBoxStyle } from '../utils';
import type { StackLayoutProps } from './types';

const EXPO_BLUR_TINTS = new Set<string>([
  'light',
  'dark',
  'default',
  'extraLight',
  'regular',
  'prominent',
  'systemUltraThinMaterial',
  'systemThinMaterial',
  'systemMaterial',
  'systemThickMaterial',
  'systemChromeMaterial',
  'systemUltraThinMaterialLight',
  'systemThinMaterialLight',
  'systemMaterialLight',
  'systemThickMaterialLight',
  'systemChromeMaterialLight',
  'systemUltraThinMaterialDark',
  'systemThinMaterialDark',
  'systemMaterialDark',
  'systemThickMaterialDark',
  'systemChromeMaterialDark',
]);

function toExpoBlurTint(blurType: string | undefined, fallback: BlurTint): BlurTint {
  if (blurType && EXPO_BLUR_TINTS.has(blurType)) {
    return blurType as BlurTint;
  }
  return fallback;
}

/**
 * Web: `liquid-glass` uses `expo-blur` (`backdrop-filter`), matching native fallback behavior.
 * Plain View + solid `overlayColor` alone does not read as glass on web.
 */
export const Stack: FC<StackLayoutProps> = ({
  viewType = 'view',
  children,
  style,
  gap,
  gapX,
  gapY,
  align = 'stretch',
  justify,
  padding,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  direction = 'column',
  wrap,
  safeArea,
  flex,
  flexGrow,
  flexShrink,
  flexBasis,
  margin,
  marginX,
  marginY,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  borderRadius,
  borderWidth,
  borderColor,
  borderTopLeftRadius,
  borderTopRightRadius,
  borderBottomLeftRadius,
  borderBottomRightRadius,
  backgroundColor,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  alignSelf,
  overflow,
  position,
  zIndex,
  bottom,
  left,
  right,
  top,
  ...rest
}) => {
  const liquidBlurMeta = rest as BlurViewProps & { overlayColor?: string };
  const overlayColor = liquidBlurMeta.overlayColor ?? 'rgba(255, 255, 255, 0.75)';

  /** Tab bar passes ~15; web maps intensity → px (`* 0.2`), so scale up to match visible frosted glass. */
  const expoIntensity = useMemo(() => {
    const v = liquidBlurMeta.intensity;
    const base = v == null ? 50 : Math.min(100, Math.max(0, Number(v)));
    return Math.min(100, base * 2.5);
  }, [liquidBlurMeta.intensity]);

  const expoTint = useMemo(
    () => toExpoBlurTint(liquidBlurMeta.tint as string | undefined, 'default'),
    [liquidBlurMeta.tint],
  );

  const ViewComponent: React.ElementType = useMemo(() => {
    if (viewType === 'gradient') {
      return LinearGradient;
    }
    return View;
  }, [viewType]);

  const insets = useSafeAreaInsets();
  const styleDirection = direction === 'column' ? styles.column : styles.row;
  const boxStyle = useMemo(() => {
    const _style = buildBoxStyle({
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      padding,
      paddingX,
      paddingY,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      margin,
      marginX,
      marginY,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      gap,
      gapX,
      gapY,
      flex,
      flexGrow,
      flexShrink,
      flexBasis,
      borderRadius,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      borderWidth,
      borderColor,
      backgroundColor,
      overflow,
      position,
      zIndex,
      bottom,
      left,
      right,
      top,
    });
    if (viewType === 'gradient') {
      delete _style.backgroundColor;
    }
    return _style;
  }, [
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    padding,
    paddingX,
    paddingY,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    margin,
    marginX,
    marginY,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    gap,
    gapX,
    gapY,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderWidth,
    borderColor,
    backgroundColor,
    overflow,
    position,
    zIndex,
    bottom,
    left,
    right,
    top,
    viewType,
  ]);

  const safeAreaStyle = useMemo(
    () =>
      safeArea
        ? {
            paddingTop: insets.top,
            paddingBottom: insets.bottom,
          }
        : undefined,
    [insets.bottom, insets.top, safeArea],
  );

  const { outerBoxStyle, innerLayoutStyle } = useMemo(() => {
    if (viewType !== 'liquid-glass') {
      return {
        outerBoxStyle: null as ViewStyle | null,
        innerLayoutStyle: null as ViewStyle | null,
      };
    }

    const {
      padding: _p,
      paddingHorizontal: _pH,
      paddingVertical: _pV,
      paddingTop: _pT,
      paddingBottom: _pB,
      paddingLeft: _pL,
      paddingRight: _pR,
      gap: _g,
      columnGap: _cG,
      rowGap: _rG,
      ...sizingStyle
    } = boxStyle as ViewStyle;

    return {
      outerBoxStyle: sizingStyle as ViewStyle,
      innerLayoutStyle: {
        flex: 1,
        padding: _p,
        paddingHorizontal: _pH,
        paddingVertical: _pV,
        paddingTop: _pT,
        paddingBottom: _pB,
        paddingLeft: _pL,
        paddingRight: _pR,
        gap: _g,
        columnGap: _cG,
        rowGap: _rG,
      } as ViewStyle,
    };
  }, [viewType, boxStyle]);

  const viewRest = useMemo(() => {
    const r = { ...(rest as Record<string, unknown>) };
    delete r.intensity;
    delete r.tint;
    delete r.overlayColor;
    delete r.effect;
    delete r.blurTarget;
    delete r.blurMethod;
    delete r.blurType;
    delete r.blurAmount;
    return r;
  }, [rest]);

  if (viewType === 'liquid-glass' && outerBoxStyle && innerLayoutStyle) {
    return (
      <View style={[{ alignSelf }, outerBoxStyle, styles.blurClip, style]} {...viewRest}>
        <BlurView
          intensity={expoIntensity}
          tint={expoTint}
          style={[StyleSheet.absoluteFill, styles.blurClip]}
        />
        <View
          pointerEvents="none"
          style={[StyleSheet.absoluteFill, styles.blurClip, { backgroundColor: overlayColor }]}
        />
        <View
          style={[
            innerLayoutStyle,
            styleDirection,
            wrap === 'wrap' && styles.wrap,
            { alignItems: align, justifyContent: justify },
            safeAreaStyle,
          ]}>
          {children}
        </View>
      </View>
    );
  }

  return (
    <ViewComponent
      style={[
        styleDirection,
        wrap === 'wrap' && styles.wrap,
        { alignItems: align, justifyContent: justify, alignSelf },
        boxStyle,
        safeAreaStyle,
        style,
      ]}
      {...rest}>
      {children}
    </ViewComponent>
  );
};

const styles = StyleSheet.create({
  column: {
    flexDirection: 'column',
  },
  row: {
    flexDirection: 'row',
  },
  wrap: {
    flexWrap: 'wrap',
  },
  blurClip: {
    overflow: 'hidden',
  },
});
